import { Block, ContentManager } from '@benzinga/content-manager';
import { safeTimeout } from '@benzinga/safe-await';
import { Session } from '@benzinga/session';

export const SCREENER_FEED_TICKER_LIMIT = 25;
export const SCREENER_FEED_PAGE_SIZE = 10;

export const getScreenerFeedTickers = (instruments: unknown): string[] => {
  if (!Array.isArray(instruments)) return [];
  const symbols = instruments
    .map(instrument => instrument?.['symbol'])
    .filter((symbol): symbol is string => typeof symbol === 'string' && symbol !== '');
  return Array.from(new Set(symbols)).slice(0, SCREENER_FEED_TICKER_LIMIT);
};

export const collectBlocksStyles = (blocks?: Block[]): Record<string, string> => {
  try {
    const collectedBlocksStyles = {};

    if (Array.isArray(blocks)) {
      blocks.forEach(block => {
        if (block.styles) {
          block.styles.forEach(blockStyle => {
            if (blockStyle.type === 'scss') {
              collectedBlocksStyles[block.blockName] = blockStyle.value;
            }
          });
          delete block.styles;
        }
      });
    }

    return collectedBlocksStyles;
  } catch {
    return {};
  }
};

export const mergeCollectedBlockStyles = (stylesCollections: Record<string, string>): string => {
  try {
    let styles = '';

    if (stylesCollections) {
      for (const key in stylesCollections) {
        if (stylesCollections[key]) {
          styles += stylesCollections[key];
        }
      }
    }

    return styles;
  } catch {
    return '';
  }
};

export const removeAdsFromBlocks = (blocks: Block[]): Block[] => {
  if (!Array.isArray(blocks)) {
    return [];
  }

  const ADS_BLOCKS = [
    'acf/investing-channel-block',
    'acf/google-ad-placement',
    'acf/dianomi-ad',
    'ad/investing-channel',
  ];

  return blocks.filter(item => {
    if (ADS_BLOCKS.includes(item.blockName)) {
      return false;
    } else if (item.blockName === 'acf/widget-container' && item.blocks) {
      item.blocks = removeAdsFromBlocks(item.blocks);
    }

    return true;
  });
};

export const findSpecificBlocks = (blocks: Block[], blockName: string): Block[] => {
  if (!Array.isArray(blocks)) return [];
  const allIndexes = blocks
    .map((block, index) => (block.blockName === blockName ? index : -1))
    .filter(index => index !== -1);
  const result = allIndexes.map(index => blocks[index]);
  return result;
};

export const getMoneyArticleByVertical = async (limit: number, session: Session, vertical_id: number) => {
  const query = {
    limit: limit,
    vertical: vertical_id,
  };
  const res = await safeTimeout(session.getManager(ContentManager).getPosts(query), 3000);
  if (res?.ok) {
    return res.ok;
  }
  return [];
};

export const deepArrayInnerBlocksSearch = (blocks: Block[], target: string): Block | null => {
  if (!Array.isArray(blocks)) return null;
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];

    if (block.blockName === target) {
      return block;
    }

    if (block.innerBlocks && block.innerBlocks.length > 0) {
      const result = deepArrayInnerBlocksSearch(block.innerBlocks, target);
      if (result) {
        return result;
      }
    }

    if (block.blocks && block.blocks.length > 0) {
      const result = deepArrayInnerBlocksSearch(block.blocks, target);
      if (result) {
        return result;
      }
    }
  }
  return null;
};

export const deepArrayChildBlocksSearch = (
  blocks: Block[],
  blockName: string,
  path: string[] = [],
): { block: Block | null; path: string[] } => {
  for (let i = 0; i < blocks.length; i++) {
    const item = blocks?.[i];
    const currentPath = [...path, `${i}`];

    if (!item) return { block: null, path };

    if (item.blockName === blockName) {
      return { block: item as Block, path: currentPath };
    }
    if (item.childBlocks && Array.isArray(item.childBlocks)) {
      const result = deepArrayChildBlocksSearch(item.childBlocks, blockName, currentPath);
      if (result.block) {
        return result;
      }
    }
  }
  return { block: null, path };
};
